import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { blogPosts, categories, type BlogPost } from '../../../mocks/blog';

// ── Helper ─────────────────────────────────────────────────────────────────
function getWhatsAppShareUrl(title: string): string {
  const pageUrl = `${window.location.origin}/blog`;
  const text = `Check out this article from MY AVA Farms: "${title}" — ${pageUrl}`;
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

// ── Copy link button ───────────────────────────────────────────────────────
function CopyLinkButton({ title }: { title: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}/blog`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // fallback for older browsers
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      aria-label="Copy article link"
      className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap border border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-600"
    >
      <i className={`text-base ${copied ? 'ri-check-line text-green-500' : 'ri-link text-gray-400'}`}></i>
      <span className={copied ? 'text-green-600' : ''}>{copied ? 'Copied!' : 'Copy link'}</span>
    </button>
  );
}

// ── WhatsApp share button ──────────────────────────────────────────────────
function WhatsAppShare({ title, compact = false }: { title: string; compact?: boolean }) {
  const [copied, setCopied] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <a
      href={getWhatsAppShareUrl(title)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      aria-label="Share on WhatsApp"
      className={`flex items-center justify-center gap-1.5 font-semibold transition-all cursor-pointer whitespace-nowrap rounded-lg
        ${compact
          ? 'w-8 h-8 bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#25D366]'
          : 'px-4 py-2.5 bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#25D366] text-sm'
        }`}
    >
      {/* WhatsApp icon via SVG path (no external lib needed) */}
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
        className={compact ? 'w-4 h-4' : 'w-4 h-4'} aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
      {!compact && <span>{copied ? 'Link copied!' : 'Share'}</span>}
    </a>
  );
}

// ── Article Modal ─────────────────────────────────────────────────────────
function ArticleModal({ post, onClose }: { post: BlogPost; onClose: () => void }) {
  const navigate = useNavigate();
  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 pt-16 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-3xl w-full mb-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header image */}
        <div className="w-full h-64 overflow-hidden rounded-t-2xl relative">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover object-top" />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-black/40 hover:bg-black/60 text-white rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          <span className="absolute bottom-4 left-4 bg-green-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
            {post.category}
          </span>
        </div>

        <div className="p-8">
          {/* Meta */}
          <div className="flex items-center gap-3 mb-4 text-xs text-gray-400">
            <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{post.date}</span>
            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
            <span className="flex items-center gap-1"><i className="ri-time-line"></i>{post.readTime}</span>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-6 leading-snug">{post.title}</h2>

          {/* Author */}
          <div className="flex items-center gap-3 mb-8 pb-6 border-b border-gray-100">
            <div className="w-10 h-10 rounded-full overflow-hidden shrink-0">
              <img src={post.authorAvatar} alt={post.author} className="w-full h-full object-cover object-top" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{post.author}</p>
              <p className="text-xs text-gray-400">{post.authorRole}</p>
            </div>
          </div>

          {/* Body */}
          <div className="space-y-4 mb-8">
            {post.paragraphs.map((p, i) => (
              <p key={i} className="text-gray-600 leading-relaxed text-sm">{p}</p>
            ))}
          </div>

          {/* Tips box */}
          {post.tips.length > 0 && (
            <div className="bg-green-50 border border-green-100 rounded-xl p-5 mb-8">
              <p className="text-sm font-bold text-green-700 mb-3 flex items-center gap-2">
                <i className="ri-lightbulb-line"></i>Key Takeaways
              </p>
              <ul className="space-y-2">
                {post.tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <i className="ri-check-line text-green-500 mt-0.5 shrink-0"></i>{tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-8">
            {post.tags.map((tag) => (
              <span key={tag} className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full">#{tag}</span>
            ))}
          </div>

          {/* CTA */}
          <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-100">
            <a
              href="https://paystack.shop/pay/v9-4hu3n50"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 min-w-[160px] bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-semibold transition-colors cursor-pointer whitespace-nowrap text-sm flex items-center justify-center gap-2"
            >
              <i className="ri-shopping-bag-line"></i>Order Moringa Powder
            </a>
            <WhatsAppShare title={post.title} />
            <CopyLinkButton title={post.title} />
            <button
              onClick={onClose}
              className="px-6 border border-gray-200 hover:bg-gray-50 text-gray-600 py-3 rounded-xl font-semibold transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Featured Card ─────────────────────────────────────────────────────────
function FeaturedCard({ post, onClick }: { post: BlogPost; onClick: () => void }) {
  return (
    <div
      className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:-translate-y-1 transition-transform duration-300 cursor-pointer grid md:grid-cols-2"
      onClick={onClick}
    >
      <div className="w-full h-64 md:h-auto overflow-hidden">
        <img src={post.image} alt={post.title} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-8 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">Featured</span>
            <span className="bg-green-50 text-green-700 text-xs font-semibold px-3 py-1 rounded-full">{post.category}</span>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3 leading-snug">{post.title}</h3>
          <p className="text-gray-500 text-sm leading-relaxed mb-6">{post.excerpt}</p>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full overflow-hidden">
              <img src={post.authorAvatar} alt={post.author} className="w-full h-full object-cover object-top" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-800">{post.author}</p>
              <p className="text-xs text-gray-400">{post.date} · {post.readTime}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <WhatsAppShare title={post.title} compact />
            <span className="flex items-center gap-1 text-green-600 text-sm font-semibold">
              Read more <i className="ri-arrow-right-line"></i>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Article Card ──────────────────────────────────────────────────────────
function ArticleCard({ post, onClick }: { post: BlogPost; onClick: () => void }) {
  return (
    <div
      className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:-translate-y-1 transition-transform duration-300 cursor-pointer flex flex-col"
      onClick={onClick}
    >
      <div className="w-full h-48 overflow-hidden">
        <img src={post.image} alt={post.title} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-3">
          <span className="bg-green-50 text-green-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">{post.category}</span>
          <span className="text-gray-300 text-xs">·</span>
          <span className="text-gray-400 text-xs">{post.readTime}</span>
        </div>
        <h3 className="text-base font-bold text-gray-900 mb-2 leading-snug flex-1">{post.title}</h3>
        <p className="text-gray-500 text-xs leading-relaxed mb-4 line-clamp-3">{post.excerpt}</p>
        <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-50">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full overflow-hidden">
              <img src={post.authorAvatar} alt={post.author} className="w-full h-full object-cover object-top" />
            </div>
            <p className="text-xs text-gray-500">{post.author}</p>
          </div>
          <div className="flex items-center gap-2">
            <p className="text-xs text-gray-400">{post.date}</p>
            <WhatsAppShare title={post.title} compact />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Blog Grid ─────────────────────────────────────────────────────────────
export default function BlogGrid() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);

  const filtered = activeCategory === 'All'
    ? blogPosts
    : blogPosts.filter((p) => p.category === activeCategory);

  const featured = filtered.find((p) => p.featured) ?? filtered[0];
  const rest = filtered.filter((p) => p.id !== featured?.id);

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 justify-center mb-12">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap border ${
                activeCategory === cat
                  ? 'bg-green-600 text-white border-green-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-green-400 hover:text-green-600'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Featured */}
        {featured && (
          <div className="mb-10">
            <FeaturedCard post={featured} onClick={() => setSelectedPost(featured)} />
          </div>
        )}

        {/* Article grid */}
        {rest.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
            {rest.map((post) => (
              <ArticleCard key={post.id} post={post} onClick={() => setSelectedPost(post)} />
            ))}
          </div>
        )}

        {/* Bottom CTA */}
        <div className="bg-green-600 rounded-2xl p-8 text-center text-white">
          <i className="ri-leaf-line text-4xl text-green-200 mb-3 block"></i>
          <h3 className="text-2xl font-bold mb-2">Ready to Try Pure Moringa Powder?</h3>
          <p className="text-green-100 text-sm mb-6 max-w-md mx-auto">
            Grown and processed right here in Ghana. Available in 50g, 100g, 250g, and 1kg — order today.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="https://paystack.shop/pay/v9-4hu3n50"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center bg-white text-green-700 hover:bg-gray-100 font-bold px-8 py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-shopping-bag-line mr-2"></i>Order Now
            </a>
            <a
              href="/products"
              className="inline-flex items-center justify-center bg-green-700 hover:bg-green-800 text-white font-semibold px-8 py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap border border-green-500"
            >
              View All Products
            </a>
          </div>
        </div>
      </div>

      {/* Article Modal */}
      {selectedPost && (
        <ArticleModal post={selectedPost} onClose={() => setSelectedPost(null)} />
      )}
    </section>
  );
}